// Affichage d'un message selon sa nature (charte D-038) : message ordinaire (citation, pièces jointes,
// modification dans les 15 minutes avec historique, masquage avec motif, accusés de lecture), notification
// automatique, décision officielle consignée (mise en évidence, jamais modifiable). Les actions proposées
// sont exactement celles que le serveur autorise pour ce message.

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageErreur } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import {
  cles,
  codeFichier,
  dateCourte,
  heureDe,
  historiqueMessage,
  masquerMessage,
  modifierMessage,
  taille,
  telechargerPieceJointe,
  type DecisionVue,
  type MessageVue,
} from './api';
import { IconeH2 } from './icones';

export interface Citation {
  id: string;
  auteur: string | null;
  extrait: string;
}

export interface ContexteMessage {
  conversationId: string;
  moi: string | undefined;
  /** Citer un message dans le message en cours de rédaction (absent en lecture seule). */
  surCiter: ((c: Citation) => void) | null;
  /** Faire défiler le fil jusqu'à un message (citation, décision corrigée). */
  surAllerA: (messageId: string) => void;
  /** Suivi des décisions du canal (titres des décisions corrigées, corrections ultérieures). */
  decisions: DecisionVue[];
}

/** Accusés de lecture : à l'auteur pour un message ordinaire, à tous les participants pour une décision. */
function Accuses({
  m,
  moi,
  pourTous = false,
}: {
  m: MessageVue;
  moi: string | undefined;
  pourTous?: boolean;
}) {
  if (!m.lecture || m.lecture.destinataires === 0) return null;
  if (!pourTous && m.auteur?.id !== moi) return null;
  const { luPar, lecteurs, destinataires } = m.lecture;
  if (lecteurs === 0) {
    return (
      <span className="msg-accuse">{m.nature === 'decision' ? 'Non encore lue' : 'Envoyé, non lu'}</span>
    );
  }
  const resume =
    destinataires === 1 && luPar[0]
      ? `Lu, ${dateCourte(luPar[0].le)}`
      : lecteurs >= destinataires
        ? `Lu par les ${lecteurs} destinataires`
        : `Lu par ${lecteurs} sur ${destinataires}`;
  if (luPar.length === 0 || destinataires === 1) return <span className="msg-accuse lu">{resume}</span>;
  return (
    <details className="msg-accuse lu">
      <summary>{resume}</summary>
      <ul>
        {luPar.map((l) => (
          <li key={l.utilisateurId}>
            {l.nom}, {dateHeure(l.le)}
          </li>
        ))}
      </ul>
    </details>
  );
}

function PiecesJointes({ m }: { m: MessageVue }) {
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  if (m.piecesJointes.length === 0) return null;
  const telecharger = (id: string) => {
    setErreur(null);
    setEnCours(id);
    telechargerPieceJointe(id)
      .catch(setErreur)
      .finally(() => setEnCours(null));
  };
  return (
    <div className="msg-pieces">
      <ul>
        {m.piecesJointes.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              className="msg-piece"
              disabled={enCours === p.id}
              onClick={() => telecharger(p.id)}
              aria-label={`Télécharger ${p.nomFichier}, ${codeFichier(p.typeFichier)}, ${taille(p.taille)}`}
            >
              <span className="msg-piece-code" aria-hidden="true">
                {codeFichier(p.typeFichier)}
              </span>
              <span className="msg-piece-texte">
                <b>{p.nomFichier}</b>
                <small>
                  {enCours === p.id
                    ? 'Préparation du lien signé'
                    : `${taille(p.taille)}, lien signé de 5 minutes`}
                </small>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {erreur !== null && <MessageErreur erreur={erreur} titre="Téléchargement refusé" />}
    </div>
  );
}

function Historique({ id }: { id: string }) {
  const h = useQuery({ queryKey: ['messagerie', 'historique', id], queryFn: () => historiqueMessage(id) });
  if (h.isLoading) return <p className="attenue msg-historique">Chargement de l’historique</p>;
  if (h.error) return <MessageErreur erreur={h.error} titre="Historique indisponible" />;
  return (
    <ol className="msg-historique" aria-label="Versions antérieures du message">
      {h.data!.map((v) => (
        <li key={v.id}>
          <span className="contexte">Modifié le {dateHeure(v.modifieLe)}. Version antérieure :</span>
          <span className="msg-texte">{v.contenuPrecedent}</span>
        </li>
      ))}
    </ol>
  );
}

function CarteDecision({ m, ctx }: { m: MessageVue; ctx: ContexteMessage }) {
  const suivi = ctx.decisions.find((d) => d.id === m.id);
  const corrigee = m.decision?.corrigeeId
    ? ctx.decisions.find((d) => d.id === m.decision!.corrigeeId)
    : undefined;
  const echeance = m.decision?.echeance ?? null;
  return (
    <li className="msg-ligne msg-decision" id={`message-${m.id}`}>
      <article
        className="msg-carte-decision"
        aria-label={`Décision officielle consignée : ${m.decision?.titre ?? ''}`}
      >
        <div className="msg-decision-tete">
          <span className="sur-titre">
            <IconeH2 nom="cadenas" taille={15} />
            Décision officielle consignée
          </span>
          <span className="etiquette">Non modifiable</span>
          {suivi && suivi.corrigeePar.length > 0 && (
            <span className="etiquette ambre">Corrigée par une décision ultérieure</span>
          )}
        </div>
        <h3>{m.decision?.titre}</h3>
        {m.decision?.corrigeeId && (
          <p className="contexte">
            Corrige{' '}
            <button type="button" className="msg-lien" onClick={() => ctx.surAllerA(m.decision!.corrigeeId!)}>
              {corrigee?.titre
                ? `la décision « ${corrigee.titre} »`
                : 'une décision consignée antérieurement'}
            </button>
          </p>
        )}
        <p className="msg-texte">{m.contenu}</p>
        <dl className="msg-decision-paires">
          <div>
            <dt>Responsable de l’exécution</dt>
            <dd>{m.decision?.responsable}</dd>
          </div>
          <div>
            <dt>Échéance</dt>
            <dd>{dateHeure(echeance)}</dd>
          </div>
          <div>
            <dt>Consignée par</dt>
            <dd>
              {m.auteur?.nom}
              {m.auteur?.organisation && <small>{m.auteur.organisation}</small>}
            </dd>
          </div>
          <div>
            <dt>Horodatage</dt>
            <dd>
              <time dateTime={m.creeLe}>{dateHeure(m.creeLe)}</time>
            </dd>
          </div>
        </dl>
        <div className="msg-pied">
          <Accuses m={m} moi={ctx.moi} pourTous />
          {ctx.surCiter && m.contenu && (
            <span className="msg-actions">
              <button
                type="button"
                className="msg-lien"
                onClick={() =>
                  ctx.surCiter!({
                    id: m.id,
                    auteur: m.auteur?.nom ?? null,
                    extrait: (m.decision?.titre ?? m.contenu!).slice(0, 160),
                  })
                }
              >
                Citer
              </button>
            </span>
          )}
        </div>
      </article>
    </li>
  );
}

export function MessageItem({
  m,
  ctx,
  cible = false,
}: {
  m: MessageVue;
  ctx: ContexteMessage;
  cible?: boolean;
}) {
  const client = useQueryClient();
  const [edition, setEdition] = useState<string | null>(null);
  const [motif, setMotif] = useState<string | null>(null);
  const [historique, setHistorique] = useState(false);
  const rafraichir = () => client.invalidateQueries({ queryKey: cles.messages(ctx.conversationId) });
  const modification = useMutation({
    mutationFn: (texte: string) => modifierMessage(m.id, texte),
    onSuccess: async () => {
      setEdition(null);
      await rafraichir();
    },
  });
  const masquage = useMutation({
    mutationFn: (texte: string) => masquerMessage(m.id, texte),
    onSuccess: async () => {
      setMotif(null);
      await rafraichir();
    },
  });
  const mien = m.auteur?.id === ctx.moi;
  const classeCible = cible ? ' msg-cible' : '';

  if (m.nature === 'notification') {
    return (
      <li className={`msg-ligne msg-systeme${classeCible}`} id={`message-${m.id}`}>
        <p className="msg-systeme-texte">
          <span className="sur-titre">Notification automatique, {heureDe(m.creeLe)}</span>
          {m.contenu}
        </p>
      </li>
    );
  }

  if (m.nature === 'decision') return <CarteDecision m={m} ctx={ctx} />;

  if (m.masque) {
    return (
      <li className={`msg-ligne${mien ? ' msg-mien' : ''}${classeCible}`} id={`message-${m.id}`}>
        <div className="msg-bulle msg-masque">
          <span className="msg-auteur">{mien ? 'Vous' : m.auteur?.nom}</span>
          <p>
            Message masqué le {dateHeure(m.masque.le)}
            {m.masque.par ? ` par ${m.masque.par}` : ''}
            {m.masque.motif ? `. Motif : ${m.masque.motif}` : ''}. Le contenu est conservé dans le journal
            d’audit.
          </p>
        </div>
      </li>
    );
  }

  const envoyerEdition = (e: FormEvent) => {
    e.preventDefault();
    if (edition?.trim()) modification.mutate(edition.trim());
  };
  const envoyerMasquage = (e: FormEvent) => {
    e.preventDefault();
    if (motif && motif.trim().length >= 3) masquage.mutate(motif.trim());
  };

  return (
    <li className={`msg-ligne${mien ? ' msg-mien' : ''}${classeCible}`} id={`message-${m.id}`}>
      <article
        className="msg-bulle"
        aria-label={`Message de ${mien ? 'vous' : (m.auteur?.nom ?? 'inconnu')}, ${dateHeure(m.creeLe)}`}
      >
        <header className="msg-entete-message">
          <b className="msg-auteur">{mien ? 'Vous' : m.auteur?.nom}</b>
          {!mien && m.auteur?.organisation && <span className="attenue">{m.auteur.organisation}</span>}
        </header>
        {m.cite && (
          <button
            type="button"
            className="msg-citation"
            onClick={() => ctx.surAllerA(m.cite!.id)}
            aria-label="Aller au message cité"
          >
            <IconeH2 nom="citation" taille={14} />
            {m.cite.extrait === null ? (
              <span className="attenue">Message cité masqué</span>
            ) : (
              <span>
                <b>{m.cite.auteur ?? 'Notification automatique'}</b> {m.cite.extrait}
              </span>
            )}
          </button>
        )}
        {edition === null ? (
          <p className="msg-texte">{m.contenu}</p>
        ) : (
          <form onSubmit={envoyerEdition} className="msg-edition">
            <label htmlFor={`edition-${m.id}`} className="visuellement-cache">
              Modifier le message
            </label>
            <textarea
              id={`edition-${m.id}`}
              value={edition}
              onChange={(e) => setEdition(e.target.value)}
              maxLength={10000}
            />
            <p className="contexte">
              La modification est possible pendant 15 minutes après l’envoi ; la version antérieure reste
              consultable.
            </p>
            <div className="actions">
              <button
                type="submit"
                className="bouton primaire"
                disabled={modification.isPending || !edition.trim()}
              >
                Enregistrer la modification
              </button>
              <button type="button" className="bouton" onClick={() => setEdition(null)}>
                Annuler
              </button>
            </div>
            {modification.error && <MessageErreur erreur={modification.error} titre="Modification refusée" />}
          </form>
        )}
        <PiecesJointes m={m} />
        <footer className="msg-pied">
          <time dateTime={m.creeLe} title={dateHeure(m.creeLe)}>
            {heureDe(m.creeLe)}
          </time>
          {m.modifie && (
            <button
              type="button"
              className="msg-lien"
              aria-expanded={historique}
              onClick={() => setHistorique((v) => !v)}
            >
              modifié{m.modifieLe ? ` à ${heureDe(m.modifieLe)}` : ''}
            </button>
          )}
          <Accuses m={m} moi={ctx.moi} />
          <span className="msg-actions">
            {ctx.surCiter && m.contenu && (
              <button
                type="button"
                className="msg-lien"
                onClick={() =>
                  ctx.surCiter!({
                    id: m.id,
                    auteur: m.auteur?.nom ?? null,
                    extrait: m.contenu!.slice(0, 160),
                  })
                }
              >
                Citer
              </button>
            )}
            {m.modifiable && edition === null && (
              <button type="button" className="msg-lien" onClick={() => setEdition(m.contenu ?? '')}>
                Modifier
              </button>
            )}
            {m.masquable && motif === null && (
              <button type="button" className="msg-lien" onClick={() => setMotif('')}>
                Masquer
              </button>
            )}
          </span>
        </footer>
        {historique && <Historique id={m.id} />}
        {motif !== null && (
          <form onSubmit={envoyerMasquage} className="msg-masquage">
            <div className="champ">
              <label htmlFor={`motif-${m.id}`}>Motif du masquage</label>
              <input
                id={`motif-${m.id}`}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                maxLength={500}
              />
              <span className="aide">
                Le message n’est jamais effacé : son contenu et le motif restent tracés.
              </span>
            </div>
            <div className="actions">
              <button
                type="submit"
                className="bouton danger"
                disabled={masquage.isPending || motif.trim().length < 3}
              >
                Masquer le message
              </button>
              <button type="button" className="bouton" onClick={() => setMotif(null)}>
                Annuler
              </button>
            </div>
            {masquage.error && <MessageErreur erreur={masquage.error} titre="Masquage refusé" />}
          </form>
        )}
      </article>
    </li>
  );
}
